import { Inject, Injectable } from '@angular/core';

import { differenceInMinutes } from 'date-fns';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of, withLatestFrom } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';

import * as fromFeedbackActions from '../../general/feedback/feedback.actions';
import { AuthService, AuthTokenService } from '../../../infrastructure';
import * as fromActions from './masterdata.actions';
import { getContracts, getContractsActionState } from './masterdata.selectors';
import { IEnvironment } from '../../../models';
import { RequestLogout } from '../login/login.actions';
import { AccountMasterDataFacade } from '../../../services/account/masterdata/masterdata.facade';
import { AccountUserFacade } from '../../../services/account/user/user.facade';
import { ErrorHandlerService } from '../../../services/error-handler/error-handler.base-service';
import { masterdataConverter } from '../../../services/account/masterdata/masterdata.converter';
import { getHasMissingMandatoryConsent } from '../user-profile';

@Injectable()
export class MasterDataEffects {
  loadMasterData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.LoadMasterData),
      withLatestFrom(this.store.select(getHasMissingMandatoryConsent)),
      switchMap(([action, hasMissingMandatoryConsent]) => {
        if (hasMissingMandatoryConsent === null || hasMissingMandatoryConsent) {
          return of({
            type: '[MasterDataEffects] Skipped - Has Missing Mandatory Consent',
            payload: action,
          });
        }
        this.userDataFacade.registerUserActivity().pipe(take(1)).subscribe();

        return this.masterDataFacade.getMasterData().pipe(
          map(masterdataConverter.fromDto),
          map(user => {
            return fromActions.LoadMasterDataSuccess({
              user,
            });
          }),
          catchError(error => {
            this.errorHandlerService.handleError(
              '(REQUEST ERROR) on masterdata.effects.ts:loadMasterData$:',
              error
            );
            if (error?.message?.includes('MANDATORY_CONSENT_FOR_USER_IS_MISSING')) {
              return [fromActions.LoadMasterDataFailed({ error })];
            }
            // Auth errors are handled by the token refresh interceptor — don't logout
            if (error?.status === 401 || error?.message?.includes('Token refresh failed')) {
              return [fromActions.LoadMasterDataFailed({ error })];
            }
            return [
              fromActions.LoadMasterDataFailed({ error }),
              RequestLogout({ reason: 'auth' }),
              fromFeedbackActions.ShowFeedbackError({
                title: 'login_failed_l',
                message: 'unexpected_error_m',
              }),
            ];
          })
        );
      })
    )
  );

  updateMasterData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.UpdateMasterData),
      switchMap(({ payload }) =>
        this.masterDataFacade.updateUserMasterdata(payload).pipe(
          switchMap(createdTicketId => [
            fromActions.UpdateMasterDataSuccess({ createdTicketId }),
            fromFeedbackActions.ShowFeedbackSuccess({
              message: 'update_masterdata.success_m',
            }),
          ]),
          catchError(error => {
            this.errorHandlerService.handleError(
              '(REQUEST ERROR) on masterdata.effects.ts:updateMasterData$:',
              error
            );
            return [
              fromFeedbackActions.ShowFeedbackError({
                message: 'update_masterdata.error_m',
              }),
              fromActions.UpdateMasterDataFailed({ error }),
            ];
          })
        )
      )
    )
  );
  loadContracts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.LoadContracts),
      withLatestFrom(
        this.store.select(getContracts),
        this.store.select(getContractsActionState),
        this.store.select(getHasMissingMandatoryConsent)
      ),
      switchMap(([action, data, actionState, hasMissingMandatoryConsent]) => {
        if (hasMissingMandatoryConsent === null || hasMissingMandatoryConsent) {
          return of({
            type: '[MasterDataEffects] Skipped - Has Missing Mandatory Consent',
            payload: action,
          });
        }
        const noData = !data || data?.length === 0;
        const doneTimeIsOld =
          actionState?.doneTime && differenceInMinutes(new Date(), actionState.doneTime) > 30;
        if (noData || doneTimeIsOld || action.forced) {
          return this.masterDataFacade.getContracts().pipe(
            map(masterdataConverter.fromContractsDto),
            map(contracts => {
              return fromActions.LoadContractsSuccess({
                contracts,
              });
            }),
            catchError(error => {
              this.errorHandlerService.handleError(
                '(REQUEST ERROR) on masterdata.effects.ts:loadContracts$:',
                error
              );
              if (error?.message?.includes('MANDATORY_CONSENT_FOR_USER_IS_MISSING')) {
                return [fromActions.LoadContractsFailed({ error })];
              }
              if (error?.message.includes('not verified')) {
                return [void this.authService.navigateToUnverifiedScreen()];
              }
              // Auth errors are handled by the token refresh interceptor — don't logout
              if (error?.status === 401 || error?.message?.includes('Token refresh failed')) {
                return [fromActions.LoadContractsFailed({ error })];
              }
              if (this.authTokenService.getToken()?.accessToken) {
                return [
                  fromActions.LoadContractsFailed({ error }),
                  RequestLogout({ reason: 'auth' }),
                ];
              }
              return [fromActions.LoadContractsFailed({ error })];
            })
          );
        } else {
          return of(fromActions.LoadContractsSuccess({ contracts: data }));
        }
      })
    )
  );
  loadContractDocuments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.LoadContractDocuments),
      switchMap(({ contractId }) =>
        this.masterDataFacade.getContractDocumentsList(contractId).pipe(
          map(documents =>
            fromActions.LoadContractDocumentsSuccess({
              documents,
            })
          ),
          catchError(error => {
            this.errorHandlerService.handleError(
              '(REQUEST ERROR) on masterdata.effects.ts:loadContractDocuments$:',
              error
            );
            return [fromActions.LoadContractDocumentsFailed({ error })];
          })
        )
      )
    )
  );
  setMovingAddressSent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.SetMovingAddressSent),
      switchMap(({ contractId, movingAddressSent }) =>
        this.masterDataFacade.setMovingAddressSent(contractId, movingAddressSent).pipe(
          switchMap(success => [
            fromActions.LoadContracts({ forced: true }),
            fromActions.SetMovingAddressSentSuccess({ success }),
          ]),
          catchError(error => {
            this.errorHandlerService.handleError(
              '(REQUEST ERROR) on masterdata.effects.ts:setMovingAddressSent$:',
              error
            );
            return [fromActions.SetMovingAddressSentError({ error })];
          })
        )
      )
    )
  );

  loadMasterdataChangeConfiguration$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.LoadMasterDataChangeConfiguration),
      switchMap(() =>
        this.masterDataFacade.getMasterdataChangeConfiguration().pipe(
          map(masterDataChangeConfiguration =>
            fromActions.LoadMasterDataChangeConfigurationSuccess({
              masterDataChangeConfiguration,
            })
          ),
          catchError(error => {
            this.errorHandlerService.handleError(
              '(REQUEST ERROR) on masterdata.effects.ts:loadMasterdataChangeConfiguration$$:',
              error
            );
            return [fromActions.LoadMasterDataChangeConfigurationError({ error })];
          })
        )
      )
    )
  );

  $sendMasterdataChangeRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.SendMasterDataChangeRequest),
      switchMap(({ payload }) => {
        return this.masterDataFacade.sendMasterdataChangeRequest(payload).pipe(
          switchMap(success => [fromActions.SendMasterDataChangeRequestSuccess({ success })]),
          catchError((error: Error) => {
            this.errorHandlerService.handleError(
              '(REQUEST ERROR) on masterdata.effects.ts:sendMasterdataChangeRequest$:',
              error
            );
            return [fromActions.SendMasterDataChangeRequestError({ error })];
          })
        );
      })
    )
  );

  $sendMasterdataChangeTicket$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.SendMasterDataChangeTicket),
      switchMap(({ payload }) =>
        this.masterDataFacade.sendMasterdataChangeTicket(payload).pipe(
          switchMap(success => [fromActions.SendMasterDataChangeTicketSuccess({ success })]),
          catchError(error => {
            this.errorHandlerService.handleError(
              '(REQUEST ERROR) on masterdata.effects.ts:sendMasterdataChangeTicket$:',
              error
            );
            return [fromActions.SendMasterDataChangeTicketError({ error })];
          })
        )
      )
    )
  );

  constructor(
    @Inject('ENVIRONMENT') private environment: IEnvironment,
    private actions$: Actions,
    private masterDataFacade: AccountMasterDataFacade,
    private authService: AuthService,
    private store: Store,
    private errorHandlerService: ErrorHandlerService,
    private authTokenService: AuthTokenService,
    private userDataFacade: AccountUserFacade
  ) {}
}
